import * as React from 'react';
import {
    Alert, Avatar, Box, Button, Card, CardContent, Chip, CircularProgress, Collapse, Divider, IconButton,
    Link as MuiLink, Stack, ToggleButton, ToggleButtonGroup, Tooltip, Typography,
} from '@mui/material';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import { Link as RouterLink } from 'react-router-dom';
import {
    BETUP_FEED_LABELS, deleteBetupFeedChannelPost, deleteBetupFeedComment, getBetupFeedChannelPostLikes,
    getBetupFeedCommentReplies, getBetupFeedPostComments, getBetupFeedWidgets, publishBetupFeedChannelPost,
} from '../../Api';
import {
    BETUP_FEED_CHANNEL_LANGUAGES, BetupFeedChannelDto, BetupFeedChannelLanguage, BetupFeedChannelPostDto,
    BetupFeedCommentDto, BetupFeedPostLikeDto, BetupFeedWidgetDataDto,
} from '../../models/dtos/BetupFeedChannelDto';
import { BetupFeedPlayerDto } from '../../models/dtos/BetupFeedPostDto';
import { ChannelLogo, channelName, LANGUAGE_LABELS, postLanguages } from './BetupFeedChannelDialogs';
import { ConfirmDialog, formatCount, formatFeedDate, loadErrorText } from './BetupFeedShared';
import { relativeTime } from './ChannelSubscriberDrawer';
import { cardPreview, liveWidgetIds } from './postWidgets';
import { matchStatus, PostArticleBody, WidgetDataProvider } from './PostWidgetViews';

/** Opens a player's card beside the page (the subscribers drawer). */
export type OpenPlayer = (userId: number, player: BetupFeedPlayerDto | null) => void;

/** The text a viewer of [lang] reads: that language, else English, else the first (the server's rule). */
export function postTextIn(post: BetupFeedChannelPostDto, lang: BetupFeedChannelLanguage | null): { lang: BetupFeedChannelLanguage | null; text: string } {
    const texts = post.texts ?? {};
    for (const candidate of [lang, 'en' as const, ...BETUP_FEED_CHANNEL_LANGUAGES]) {
        if (candidate && (texts[candidate] ?? '').trim()) return { lang: candidate, text: texts[candidate]! };
    }
    return { lang: null, text: post.text ?? '' };
}

export type PostState = 'published' | 'draft' | 'scheduled' | 'deleted';

export function postState(post: BetupFeedChannelPostDto): PostState {
    if (post.deleted) return 'deleted';
    if (post.draft) return post.will_publish_at ? 'scheduled' : 'draft';
    return 'published';
}

function StateChip({ post }: { post: BetupFeedChannelPostDto }) {
    switch (postState(post)) {
        case 'draft': return <Chip size="small" label="Draft" />;
        case 'scheduled': return <Chip size="small" color="info" variant="outlined" label={`Scheduled · ${formatFeedDate(post.will_publish_at)}`} />;
        case 'deleted': return <Chip size="small" color="error" variant="outlined" label="Deleted" />;
        default: return null;
    }
}

function LinkedStrip({ post }: { post: BetupFeedChannelPostDto }) {
    const { match, team, user } = post;
    if (!match && !team && !user) return null;
    return (
        <Stack direction="row" gap={1} flexWrap="wrap">
            {match && (
                <Chip variant="outlined" clickable component={RouterLink} to={`/matches/${match.id}`}
                    avatar={<Avatar src={match.home_team?.photo_url ?? undefined} />}
                    label={`${match.home_team?.name ?? '?'} – ${match.away_team?.name ?? '?'} · ${matchStatus(match).text}` +
                        (match.bets_count != null ? ` · ${formatCount(match.bets_count)} bets` : '') +
                        (match.predictions_count ? ` · ${formatCount(match.predictions_count)} predictions` : '')} />
            )}
            {team && <Chip variant="outlined" avatar={<Avatar src={team.photo_url ?? undefined} />} label={`Team: ${team.name ?? `#${team.id}`}`} />}
            {user && (
                <Chip variant="outlined" clickable component={RouterLink} to={`/users/${user.id}`}
                    avatar={<Avatar src={user.photo_url ?? undefined}>{(user.name ?? '?').slice(0, 1)}</Avatar>}
                    label={`Names: ${user.name ?? `#${user.id}`}`} />
            )}
        </Stack>
    );
}

const widgetCache = new Map<string, { at: number; data: BetupFeedWidgetDataDto }>();
const WIDGET_CACHE_MS = 60_000;

/** The live widgets' data of [text] in [lang] — fetched once the article is open, kept 60 s. */
function useWidgetData(text: string, lang: string, enabled: boolean) {
    const ids = React.useMemo(() => liveWidgetIds([text]), [text]);
    const key = `${lang}|${ids.matchIds.join(',')}|${ids.userIds.join(',')}|${ids.teamIds.join(',')}`;
    const empty = ids.matchIds.length + ids.userIds.length + ids.teamIds.length === 0;
    const [state, setState] = React.useState<{ data: BetupFeedWidgetDataDto | null; loading: boolean; failed: boolean }>({ data: null, loading: false, failed: false });
    React.useEffect(() => {
        if (!enabled) return;
        if (empty) { setState({ data: { matches: {}, users: {}, teams: {} }, loading: false, failed: false }); return; }
        const cached = widgetCache.get(key);
        if (cached && Date.now() - cached.at < WIDGET_CACHE_MS) { setState({ data: cached.data, loading: false, failed: false }); return; }
        let cancelled = false;
        setState({ data: null, loading: true, failed: false });
        getBetupFeedWidgets(ids, lang).then(data => {
            if (cancelled) return;
            if (data) widgetCache.set(key, { at: Date.now(), data });
            setState({ data, loading: false, failed: data == null });
        });
        return () => { cancelled = true; };
    }, [key, empty, enabled]); // eslint-disable-line react-hooks/exhaustive-deps
    return state;
}

type Panel = 'likes' | 'comments' | null;

/**
 * A channel post on the channel page: the preview (widgets left out, the text cut at 256 characters) that expands in
 * place into the full article with its widgets, and who liked / commented on it. Comments can be deleted here.
 */
export default function ChannelPostCard({ post, channel, lang, defaultExpanded = false, onEdit, onChanged, onOpenPlayer }: {
    post: BetupFeedChannelPostDto;
    channel: BetupFeedChannelDto | null;
    /** The page's reading language (null = English first). */
    lang: BetupFeedChannelLanguage | null;
    defaultExpanded?: boolean;
    onEdit: ((post: BetupFeedChannelPostDto) => void) | null;
    /** After publish / delete (the post as it is now) or a deleted comment (`commentsDelta`). */
    onChanged: (post: BetupFeedChannelPostDto, message: string) => void;
    onOpenPlayer: OpenPlayer;
}) {
    const [expanded, setExpanded] = React.useState(defaultExpanded);
    const [ownLang, setOwnLang] = React.useState<BetupFeedChannelLanguage | null>(null);
    const [panel, setPanel] = React.useState<Panel>(null);
    const [confirm, setConfirm] = React.useState<'publish' | 'delete' | null>(null);
    const [error, setError] = React.useState<string | null>(null);
    const [busy, setBusy] = React.useState(false);
    const [comments, setComments] = React.useState(post.comments_count);

    React.useEffect(() => { setComments(post.comments_count); }, [post.comments_count]);
    React.useEffect(() => { setOwnLang(null); }, [lang]);
    React.useEffect(() => { if (defaultExpanded) setExpanded(true); }, [defaultExpanded]);

    const shown = postTextIn(post, ownLang ?? lang);
    const preview = cardPreview(shown.text);
    const languages = postLanguages(post);
    const widgetState = useWidgetData(shown.text, shown.lang ?? 'en', expanded);

    const act = async (action: 'publish' | 'delete') => {
        if (!channel) return;
        setBusy(true);
        const result = action === 'publish'
            ? await publishBetupFeedChannelPost(channel.id, post.id)
            : await deleteBetupFeedChannelPost(channel.id, post.id);
        setBusy(false);
        setConfirm(null);
        if (result.error != null) { setError(result.error || `Could not ${action} the post.`); return; }
        onChanged(action === 'publish' ? ((result.data as BetupFeedChannelPostDto | null) ?? { ...post, draft: false, will_publish_at: null }) : { ...post, deleted: true },
            action === 'publish' ? `Post #${post.id} published.` : `Post #${post.id} deleted.`);
    };

    const togglePanel = (next: Panel) => setPanel(current => (current === next ? null : next));

    return (
        <Card variant="outlined" sx={{ opacity: post.deleted ? 0.7 : 1 }}>
            <CardContent sx={{ pb: '12px !important' }}>
                <Stack direction="row" alignItems="center" gap={1.25}>
                    {channel ? <ChannelLogo channel={channel} size={36} /> : <Avatar sx={{ width: 36, height: 36 }} />}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="subtitle2" noWrap>{channel ? channelName(channel) : 'Channel'}</Typography>
                        <Tooltip title={formatFeedDate(post.created_at)}>
                            <Typography variant="caption" color="text.secondary">
                                {relativeTime(post.created_at) || formatFeedDate(post.created_at)} · #{post.id}
                            </Typography>
                        </Tooltip>
                    </Box>
                    <StateChip post={post} />
                    {post.price != null && <Chip size="small" color="warning" icon={<LockOutlinedIcon />} label={`${formatCount(post.price)} betcoins`} />}
                    {!post.deleted && channel && (
                        <Stack direction="row">
                            {onEdit && <Tooltip title="Edit"><IconButton size="small" onClick={() => onEdit(post)}><EditOutlinedIcon fontSize="small" /></IconButton></Tooltip>}
                            {post.draft && <Tooltip title="Publish now"><IconButton size="small" color="primary" disabled={busy} onClick={() => setConfirm('publish')}><SendOutlinedIcon fontSize="small" /></IconButton></Tooltip>}
                            <Tooltip title="Delete post"><IconButton size="small" disabled={busy} onClick={() => setConfirm('delete')}><DeleteOutlineIcon fontSize="small" /></IconButton></Tooltip>
                        </Stack>
                    )}
                </Stack>

                {error && <Alert severity="error" sx={{ mt: 1.5 }} onClose={() => setError(null)}>{error}</Alert>}

                {expanded && languages.length > 1 && (
                    <ToggleButtonGroup size="small" exclusive value={shown.lang} sx={{ mt: 1.5, flexWrap: 'wrap' }}
                        onChange={(_, value) => value && setOwnLang(value)}>
                        {languages.map(l => (
                            <ToggleButton key={l} value={l} sx={{ py: 0.25, px: 1 }}>
                                <Tooltip title={LANGUAGE_LABELS[l]}><span>{l.toUpperCase()}</span></Tooltip>
                            </ToggleButton>
                        ))}
                    </ToggleButtonGroup>
                )}

                <Box sx={{ mt: 1.5 }}>
                    {expanded ? (
                        <>
                            <WidgetDataProvider value={{ data: widgetState.data, loading: widgetState.loading }}>
                                {shown.text.trim()
                                    ? <PostArticleBody text={shown.text} />
                                    : <Typography variant="body2" color="text.secondary">No text.</Typography>}
                            </WidgetDataProvider>
                            {widgetState.failed && <Alert severity="warning" sx={{ mt: 1 }}>Couldn’t load the live widgets’ data.</Alert>}
                            {preview.heldBack && <Button size="small" sx={{ mt: 1, ml: -1 }} onClick={() => setExpanded(false)}>Show less</Button>}
                        </>
                    ) : (
                        <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                            {preview.text}{preview.heldBack && '… '}
                            {preview.heldBack && (
                                <MuiLink component="button" variant="body1" underline="hover" sx={{ fontWeight: 600, verticalAlign: 'baseline' }}
                                    onClick={() => setExpanded(true)}>
                                    Read more{preview.widgets > 0 ? ` · ${preview.widgets} widget${preview.widgets === 1 ? '' : 's'}` : ''}
                                </MuiLink>
                            )}
                        </Typography>
                    )}
                </Box>

                {post.image_url && (
                    <Box component="img" src={post.image_url} alt="" loading="lazy"
                        sx={{ display: 'block', width: '100%', maxHeight: expanded ? 'none' : 280, objectFit: 'cover', borderRadius: 1, mt: 1.5 }} />
                )}
                <Box sx={{ mt: 1.5 }}><LinkedStrip post={post} /></Box>

                <Divider sx={{ mt: 1.5, mb: 1 }} />
                <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
                    <Button size="small" startIcon={<FavoriteBorderIcon />} variant={panel === 'likes' ? 'contained' : 'text'}
                        disableElevation onClick={() => togglePanel('likes')}>
                        {formatCount(post.likes_count)} like{post.likes_count === 1 ? '' : 's'}
                    </Button>
                    <Button size="small" startIcon={<ChatBubbleOutlineIcon />} variant={panel === 'comments' ? 'contained' : 'text'}
                        disableElevation onClick={() => togglePanel('comments')}>
                        {formatCount(comments)} comment{comments === 1 ? '' : 's'}
                    </Button>
                    {post.views_count != null && (
                        <Tooltip title="Times players opened the full text in the app (Read more). Counts reads, not distinct players; only operators see it.">
                            <Stack direction="row" alignItems="center" gap={0.75} sx={{ px: 0.625, color: 'text.secondary' }}>
                                <VisibilityOutlinedIcon fontSize="small" />
                                <Typography variant="button">
                                    {formatCount(post.views_count)} view{post.views_count === 1 ? '' : 's'}
                                </Typography>
                            </Stack>
                        </Tooltip>
                    )}
                    {post.price != null && (
                        <Typography variant="body2" color="text.secondary">
                            {formatCount(post.sales_count ?? 0)} sold · {formatCount(post.sales_total ?? 0)} betcoins
                        </Typography>
                    )}
                    <Box sx={{ flex: 1 }} />
                    <Tooltip title={languages.map(l => LANGUAGE_LABELS[l]).join(', ') || 'No text'}>
                        <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase' }}>
                            {languages.join(' · ') || '—'}
                        </Typography>
                    </Tooltip>
                </Stack>

                <Collapse in={panel === 'likes'} unmountOnExit>
                    {channel && <LikesPanel channelId={channel.id} postId={post.id} onOpenPlayer={onOpenPlayer} />}
                </Collapse>
                <Collapse in={panel === 'comments'} unmountOnExit>
                    <CommentsPanel postId={post.id} onOpenPlayer={onOpenPlayer}
                        onDeleted={count => setComments(c => Math.max(0, c - count))} />
                </Collapse>
            </CardContent>

            <ConfirmDialog
                open={confirm != null}
                title={confirm === 'publish' ? `Publish post #${post.id} now?` : `Delete post #${post.id}?`}
                text={confirm === 'publish'
                    ? 'Players see it from now on, at the top of the feeds; subscribers with notifications on get a push.'
                    : 'It leaves every feed. Likes, comments and sales stay recorded.'}
                confirmLabel={confirm === 'publish' ? 'Publish' : 'Delete'}
                confirmColor={confirm === 'publish' ? 'primary' : 'error'}
                onConfirm={() => confirm && act(confirm)}
                onClose={() => setConfirm(null)}
            />
        </Card>
    );
}

// ---- Likes ----

const LIKES_PAGE = 25;

function PlayerName({ userId, player, onOpenPlayer }: { userId: number; player: BetupFeedPlayerDto | { id: number; name: string | null } | null; onOpenPlayer: OpenPlayer }) {
    const name = player?.name || `#${userId}`;
    return (
        <MuiLink component="button" variant="body2" underline="hover" sx={{ fontWeight: 600, textAlign: 'left' }}
            onClick={() => onOpenPlayer(userId, player && 'level' in player ? player : null)}>
            {name}
        </MuiLink>
    );
}

function LikesPanel({ channelId, postId, onOpenPlayer }: { channelId: number; postId: number; onOpenPlayer: OpenPlayer }) {
    const [items, setItems] = React.useState<BetupFeedPostLikeDto[]>([]);
    const [total, setTotal] = React.useState(0);
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState<string | null>(null);

    const load = React.useCallback(async (offset: number) => {
        setLoading(true);
        const page = await getBetupFeedChannelPostLikes(channelId, postId, offset, LIKES_PAGE);
        setLoading(false);
        if (page == null) { setError(loadErrorText(BETUP_FEED_LABELS.postLikes, 'Failed to load the likes.')); return; }
        setError(null);
        setItems(prev => (offset === 0 ? page.items : [...prev, ...page.items]));
        setTotal(page.totalItems);
    }, [channelId, postId]);

    React.useEffect(() => { load(0); }, [load]);

    return (
        <Box sx={{ mt: 1.5, pl: 1 }}>
            {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
            {items.length === 0 && !loading && !error && <Typography variant="body2" color="text.secondary">Nobody liked it yet.</Typography>}
            <Stack gap={1}>
                {items.map(like => (
                    <Stack key={`${like.user_id}-${like.liked_at}`} direction="row" alignItems="center" gap={1.25}>
                        <Avatar src={like.user?.photo_url ?? undefined} sx={{ width: 32, height: 32 }}>{(like.user?.name ?? '?').slice(0, 1)}</Avatar>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <PlayerName userId={like.user_id} player={like.user} onOpenPlayer={onOpenPlayer} />
                            <Typography variant="caption" color="text.secondary" component="div">
                                #{like.user_id}{like.user?.level != null ? ` · Lv ${like.user.level}` : ''}{like.user?.country_iso ? ` · ${like.user.country_iso}` : ''}
                                {like.user == null ? ' · deleted player' : ''}
                            </Typography>
                        </Box>
                        <Tooltip title={formatFeedDate(like.liked_at)}>
                            <Typography variant="caption" color="text.secondary">{relativeTime(like.liked_at)}</Typography>
                        </Tooltip>
                    </Stack>
                ))}
            </Stack>
            {loading && <CircularProgress size={20} sx={{ mt: 1 }} />}
            {!loading && items.length < total && (
                <Button size="small" sx={{ mt: 1 }} onClick={() => load(items.length)}>Show more ({formatCount(total - items.length)})</Button>
            )}
        </Box>
    );
}

// ---- Comments ----

const COMMENTS_PAGE = 20;

function useCommentPages(loader: (token: string | null) => Promise<{ content: BetupFeedCommentDto[]; pageOptions?: { continuationToken?: string | null } | null } | null>) {
    const [items, setItems] = React.useState<BetupFeedCommentDto[]>([]);
    const [token, setToken] = React.useState<string | null>(null);
    const [more, setMore] = React.useState(false);
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState<string | null>(null);

    const load = React.useCallback(async (from: string | null) => {
        setLoading(true);
        const page = await loader(from);
        setLoading(false);
        if (page == null) { setError(loadErrorText(BETUP_FEED_LABELS.postComments, 'Failed to load the comments.')); return; }
        setError(null);
        const content = page.content ?? [];
        setItems(prev => {
            const base = from == null ? [] : prev;
            const seen = new Set(base.map(c => c.message.id));
            return [...base, ...content.filter(c => !seen.has(c.message.id))];
        });
        const next = page.pageOptions?.continuationToken ?? null;
        setToken(next);
        setMore(next != null && content.length >= COMMENTS_PAGE);
    }, [loader]);

    React.useEffect(() => { load(null); }, [load]);
    const remove = (id: number) => setItems(prev => prev.filter(c => c.message.id !== id));
    return { items, more, loading, error, loadMore: () => load(token), remove };
}

function likesOf(comment: BetupFeedCommentDto): number {
    return (comment.reactions ?? []).reduce((total, r) => total + (r.count ?? 0), 0);
}

function CommentsPanel({ postId, onOpenPlayer, onDeleted }: { postId: number; onOpenPlayer: OpenPlayer; onDeleted: (count: number) => void }) {
    const loader = React.useCallback((token: string | null) => getBetupFeedPostComments(postId, token, COMMENTS_PAGE), [postId]);
    const pages = useCommentPages(loader);
    const [notice, setNotice] = React.useState<string | null>(null);
    return (
        <Box sx={{ mt: 1.5, pl: 1 }}>
            {pages.error && <Alert severity="error" sx={{ mb: 1 }}>{pages.error}</Alert>}
            {notice && <Alert severity="success" sx={{ mb: 1 }} onClose={() => setNotice(null)}>{notice}</Alert>}
            {pages.items.length === 0 && !pages.loading && !pages.error && <Typography variant="body2" color="text.secondary">No comments.</Typography>}
            <Stack gap={1.5}>
                {pages.items.map(comment => (
                    <CommentRow key={comment.message.id} comment={comment} onOpenPlayer={onOpenPlayer} onAnyDeleted={onDeleted}
                        onDeleted={count => {
                            pages.remove(comment.message.id);
                            setNotice(`Comment deleted${count > 1 ? ` with ${count - 1} repl${count === 2 ? 'y' : 'ies'}` : ''}. Players see the new count within seconds.`);
                        }} />
                ))}
            </Stack>
            {pages.loading && <CircularProgress size={20} sx={{ mt: 1 }} />}
            {!pages.loading && pages.more && <Button size="small" sx={{ mt: 1 }} onClick={pages.loadMore}>Older comments</Button>}
        </Box>
    );
}

function CommentRow({ comment, depth = 0, onOpenPlayer, onDeleted, onAnyDeleted }: {
    comment: BetupFeedCommentDto;
    depth?: number;
    onOpenPlayer: OpenPlayer;
    /** This comment went, with [count] - 1 replies (its list drops it). */
    onDeleted: (count: number) => void;
    /** Any comment of the post went, at any depth: the post's total. */
    onAnyDeleted: (count: number) => void;
}) {
    const { message } = comment;
    const [showReplies, setShowReplies] = React.useState(false);
    const [confirm, setConfirm] = React.useState(false);
    const [busy, setBusy] = React.useState(false);
    const [error, setError] = React.useState<string | null>(null);
    const [replies, setReplies] = React.useState(comment.commentCount);
    const likes = likesOf(comment);
    const author = message.user;

    const remove = async () => {
        setBusy(true);
        const result = await deleteBetupFeedComment(message.id);
        setBusy(false);
        setConfirm(false);
        if (result.error != null) { setError(result.error); return; }
        const count = result.deleted ?? 1 + replies;
        onDeleted(count);
        onAnyDeleted(count);
    };

    return (
        <Box sx={{ pl: depth > 0 ? 2 : 0, borderLeft: depth > 0 ? 2 : 0, borderColor: 'divider' }}>
            <Stack direction="row" gap={1.25} alignItems="flex-start">
                <Avatar src={author?.avatarUrl ?? undefined} sx={{ width: 32, height: 32 }}>{(author?.name ?? '?').slice(0, 1)}</Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Stack direction="row" gap={1} alignItems="baseline" flexWrap="wrap">
                        {author ? <PlayerName userId={author.id} player={author} onOpenPlayer={onOpenPlayer} /> : <Typography variant="body2" color="text.secondary">Deleted player</Typography>}
                        <Tooltip title={formatFeedDate(message.sendDate)}>
                            <Typography variant="caption" color="text.secondary">{relativeTime(message.sendDate)}</Typography>
                        </Tooltip>
                    </Stack>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', mt: 0.25 }}>{message.message}</Typography>
                    <Stack direction="row" gap={1.5} alignItems="center" sx={{ mt: 0.25 }}>
                        {likes > 0 && <Typography variant="caption" color="text.secondary">♥ {formatCount(likes)}</Typography>}
                        {replies > 0 && (
                            <MuiLink component="button" variant="caption" underline="hover" onClick={() => setShowReplies(v => !v)}>
                                {showReplies ? 'Hide replies' : `${formatCount(replies)} repl${replies === 1 ? 'y' : 'ies'}`}
                            </MuiLink>
                        )}
                    </Stack>
                    {error && <Alert severity="error" sx={{ mt: 0.5 }} onClose={() => setError(null)}>{error}</Alert>}
                </Box>
                <Tooltip title="Delete comment">
                    <span><IconButton size="small" disabled={busy} onClick={() => setConfirm(true)}><DeleteOutlineIcon fontSize="small" /></IconButton></span>
                </Tooltip>
            </Stack>
            {showReplies && (
                <Box sx={{ mt: 1, ml: 5 }}>
                    <Replies messageId={message.id} depth={depth + 1} onOpenPlayer={onOpenPlayer} onAnyDeleted={onAnyDeleted}
                        onDeleted={count => setReplies(r => Math.max(0, r - count))} />
                </Box>
            )}
            <ConfirmDialog
                open={confirm}
                title="Delete this comment?"
                text={`${author?.name ?? 'The player'}’s comment${replies > 0 ? ` and its ${formatCount(replies)} repl${replies === 1 ? 'y' : 'ies'}` : ''} will be removed for everyone. This can't be undone.`}
                confirmLabel="Delete"
                confirmColor="error"
                onConfirm={remove}
                onClose={() => setConfirm(false)}
            />
        </Box>
    );
}

function Replies({ messageId, depth, onOpenPlayer, onDeleted, onAnyDeleted }: {
    messageId: number;
    depth: number;
    onOpenPlayer: OpenPlayer;
    /** A direct reply went (with [count] - 1 of its own replies). */
    onDeleted: (count: number) => void;
    onAnyDeleted: (count: number) => void;
}) {
    const loader = React.useCallback((token: string | null) => getBetupFeedCommentReplies(messageId, token, COMMENTS_PAGE), [messageId]);
    const pages = useCommentPages(loader);
    return (
        <Stack gap={1.25}>
            {pages.error && <Alert severity="error">{pages.error}</Alert>}
            {pages.items.map(reply => (
                <CommentRow key={reply.message.id} comment={reply} depth={depth} onOpenPlayer={onOpenPlayer} onAnyDeleted={onAnyDeleted}
                    onDeleted={count => { pages.remove(reply.message.id); onDeleted(count); }} />
            ))}
            {pages.loading && <CircularProgress size={18} />}
            {!pages.loading && pages.more && <Button size="small" onClick={pages.loadMore}>Older replies</Button>}
        </Stack>
    );
}
